<template>
    <div :id="`${id}_cont`" class="form-group pb-2" v-if="showContent">
        <template v-if="!editMode">
            <div class="d-flex justify-content-between">
                <div class="d-flex">
                    <label v-if="title" :for="id" class="title form-label">{{ title }}</label>
                    <template v-if="moreInfo" >
                        <p :title="moreInfoText" class="m-0"><i class="ri-information-2-line"></i></p>
                    </template>
                    <VsudInfoTooltip v-if="tooltip" class="vsud-select__tooltip" :text="tooltip" :position="tooltipPosition" />
                    <p class="invalid-feedback required ps-2" v-if="asterisk"> * </p>
                </div>
                <template v-if="!disableValidation && (feedback === 'invalid' || feedback === 'valid')">
                    <p v-if="feedback === 'invalid'" class="invalid-feedback">{{ errorMsg }}</p>
                    <p v-else-if="feedback === 'valid'" class="valid-feedback">{{ validMsg }}</p>
                </template>
                <template v-else-if="isRequired">
                    <p class="invalid-feedback required">Dato obligatorio</p>
                </template>
                <template v-else-if="!isRequired && showRequired">
                    <p class="invalid-feedback optional">Dato opcional</p>
                </template>
            </div>
        </template>
        <template v-else-if="editMode">
            <div class="d-flex">
                <label v-if="title" :for="id" class="title">{{ title }}</label>
                <template v-if="isRequired && !errorMsgForm && editMode">
                    <p class="invalid-feedback required ms-1">*</p>
                </template>
                <VsudInfoTooltip v-if="tooltip" class="vsud-select__tooltip" :text="tooltip" :position="tooltipPosition" />
            </div>
        </template>
        <template v-if="idName">
            <select class="form-select" :name="name" :id="id" ref="selectEl" :editMode="editMode"
                @change="handleChange($event.target.value)" :value="modelValue" :required="isRequired"
                :disabled="disableSelect">
                <option v-if="placeholder" value="" >{{ placeholder }}</option>
                <option v-for="item in items" :key="item.id" :value="item.id">
                    {{ item.id }}
                </option>
            </select>
        </template>
        <template v-else>
            <select class="form-select" :name="name" :id="id" ref="selectEl" :editMode="editMode"
                @change="handleChange($event.target.value)" :value="modelValue" :required="isRequired"
                :disabled="disableSelect">
                <option v-if="placeholder" value="">{{ placeholder }}</option>
                <option v-for="item in items" :key="item.id" :value="item.id">
                    {{ item.name_and_company || item.name || item.plate || item.text || item.command }}
                </option>
            </select>
        </template>
    </div>
    <!-- Loading -->
    <div :id="`${id}_cont`" v-else>
        <div class="form-group pb-2">
            <label v-if="title" class="title placeholder-glow col-3">
                <span class="placeholder col-12"></span>
            </label>
            <div class="placeholder-glow">
                <input class="form-control placeholder col-12" />
            </div>
        </div>
    </div>
</template>

<script>
import VsudInfoTooltip from "@/components/VsudInfoTooltip.vue";

export default {
    name: "VsudSelect",
    components: { VsudInfoTooltip },
    props: {
        title: {
            type: String,
            default: "",
        },
        name: {
            type: String,
            default: "",
        },
        id: {
            type: String,
            default: "",
        },
        placeholder: {
            type: String,
            default: "",
        },
        value: {
            type: String,
            default: "",
        },
        items: {
            type: Array,
            default: () => [],
        },
        errorMsg: {
            type: String,
            default: 'Valor incorrecto o nulo',
        },
        validMsg: {
            type: String,
            default: 'Completado correctamente',
        },
        isRequired: Boolean,
        disableSelect: {
            type: Boolean,
            default: false,
        },
        disableValidation: {
            type: Boolean,
            default: false,
        },
        idName: {
            type: Boolean,
            default: false,
        },
        modelValue: {
            type: [String, Number],
            default: " "
        },
        showRequired: {
            type: Boolean,
            default: true,
        },
        editMode: {
            type: Boolean,
            default: false,
        },
        moreInfo: {
            type: Boolean,
            default: false,
        },
        moreInfoText: {
            type: String,
        },
        asterisk: {
            type: Boolean,
            default: false,
        },
        tooltip: {
            type: String,
            default: '',
        },
        tooltipPosition: {
            type: String,
            default: 'top',
            validator: (v) => ['top', 'bottom', 'left', 'right'].includes(v),
        },
        // Se muestra al montarse, sin el esqueleto de carga (p. ej. en un modal o un filtro).
        skipLoading: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            showContent: this.skipLoading,
            minLoadTime: 1500,
            loadStart: null,
            feedback: '',
        };
    },
    mounted() {
        if (this.skipLoading) return;
        this.loadStart = Date.now();
        window.addEventListener("load", this.onLoad);
        setTimeout(() => {
            this.showContent = true;
        }, this.minLoadTime);
    },
    beforeUnmount() {
        window.removeEventListener("load", this.onLoad);
    },
    watch: {
        modelValue(newVal) {
            if (newVal === "" && this.isRequired) {
                this.feedback = "invalid"; 
            } else {
                this.feedback = "valid"; 
            }
            this.$emit("feedback", this.feedback);
        },
    },
    methods: {
        onLoad() {
            const loadTime = Date.now() - this.loadStart;
            if (loadTime >= this.minLoadTime) {
                this.showContent = true;
            } else {
                setTimeout(() => {
                    this.showContent = true;
                }, this.minLoadTime - loadTime);
            }
        },
        handleChange(newValue) {
            // Emitir el evento solo si el valor ha cambiado
            if (this.modelValue !== newValue) {
                this.$emit('update:modelValue', newValue);
            }

        },
    },
};
</script>


<style scoped>
.title {
    color: var(--black);
    font-size: 0.9rem;
    font-weight: 500;
}

select {
    border: 1px solid var(--light-gray);
    border-radius: 0.7rem;
}

select:focus {
    border: 1px solid var(--blue);
    outline: 0;
    box-shadow: none;
}

.was-validated .form-select:valid:focus,
.form-select.is-valid:focus,
.was-validated .form-select:invalid:focus,
.form-select.is-invalid:focus {
    box-shadow: none;
}

.optional {
    color: var(--gray-text);
}
.ri-information-2-line {
    color: var(--blue);
    position: relative;
    bottom: 3px;
    padding-left: 0.3rem;
    cursor: pointer;
}

/* Misma posición que tenía el tooltip propio: junto al título, un poco más arriba. */
.vsud-select__tooltip {
    position: relative;
    bottom: 3px;
    align-self: center;
    margin-left: -0.1rem;
    margin-bottom: 15px;
    padding-left: 0.3rem;
}
</style>
